import React, { useEffect, useState, useMemo, useRef } from 'react';
import { useHistory, useParams } from 'react-router-dom';
import { useDispatch, useSelector } from 'react-redux';
import { useFormik } from 'formik';
import { MenuItem } from '@material-ui/core';
import { FaFileInvoiceDollar } from 'react-icons/fa';

import Paper from 'components/Paper';
import TextField from 'components/TextField';
import Button from 'components/Button';
import AccountsPayableActions from 'store/ducks/accountsPayable';
import NaturezasActions from 'store/ducks/naturezas';
import FornecedoresActions from 'store/ducks/fornecedores';
import { fornecedoresAPI } from 'services/apiRest';
import { schema } from '../components/Modal/schemaValidation';

import {
  Container,
  WrapperBody,
  WrapperContent,
  WrapperBottom,
  WrapperInput,
  InputRow,
  AutocompleteWrapper,
  AutocompleteDropdown,
  AutocompleteItem,
} from './styles';

function ContaPagarForm() {
  const dispatch = useDispatch();
  const history = useHistory();
  const { id } = useParams();
  const { accounts, isLoading } = useSelector((state) => state.accountsPayable);
  const { naturezas: naturezasState } = useSelector((state) => state.naturezas);
  const isEdit = !!id;
  const [naturezaInput, setNaturezaInput] = useState('');
  const [showDropdownNatureza, setShowDropdownNatureza] = useState(false);
  const dropdownNaturezaRef = useRef(null);
  const [fornecedorInput, setFornecedorInput] = useState('');
  const [showDropdownFornecedor, setShowDropdownFornecedor] = useState(false);
  const dropdownFornecedorRef = useRef(null);
  const [fornecedores, setFornecedores] = useState([]);
  const [fornecedorSelecionado, setFornecedorSelecionado] = useState(null);

  // Converter naturezas de Immutable para array normal
  const naturezas = useMemo(() => {
    if (!naturezasState) return [];
    
    if (Array.isArray(naturezasState)) {
      return naturezasState;
    }
    
    if (naturezasState && typeof naturezasState === 'object') {
      if (typeof naturezasState.asMutable === 'function') {
        try {
          const mutable = naturezasState.asMutable({ deep: true });
          return Array.isArray(mutable) ? mutable : [];
        } catch (e) {
          console.warn('Erro ao converter Immutable:', e);
        }
      }
      
      if (typeof naturezasState.length === 'number') {
        try {
          return Array.from(naturezasState);
        } catch (e) {
          console.warn('Erro ao converter com Array.from:', e);
        }
      }
    }
    
    return [];
  }, [naturezasState]);

  // Carregar naturezas
  useEffect(() => {
    dispatch(NaturezasActions.getNaturezasRequest());
  }, [dispatch]);

  // Carregar fornecedores
  useEffect(() => {
    const carregarFornecedores = async () => {
      try {
        const fornecedoresList = await fornecedoresAPI.listar();
        setFornecedores(fornecedoresList);
      } catch (error) {
        console.error('Erro ao carregar fornecedores:', error);
      }
    };
    carregarFornecedores();
  }, []);

  // Buscar conta se estiver editando
  useEffect(() => {
    if (isEdit && accounts.length === 0) {
      dispatch(AccountsPayableActions.getAccountsPayableRequest());
    }
  }, [dispatch, isEdit, accounts.length]);

  const account = isEdit ? accounts.find((item) => item.id === id) : null;

  const formik = useFormik({
    initialValues: {
      description: account?.description || '',
      supplier: account?.supplier || '',
      supplierId: account?.supplierId || '',
      category: account?.category || '',
      value: account?.value || '',
      dueDate: account?.dueDate || '',
      paymentDate: account?.paymentDate || '',
      status: account?.status || 'pending',
      notes: account?.notes || '',
      naturezaId: account?.naturezaId || '',
      formaPagamento: account?.formaPagamento || '',
    },
    validationSchema: schema,
    enableReinitialize: true,
    onSubmit: (values) => {
      // Converter naturezaId - é UUID (string), não número
      let naturezaId = null;
      if (values.naturezaId !== undefined && values.naturezaId !== null && values.naturezaId !== '') {
        if (typeof values.naturezaId === 'string') {
          const trimmed = values.naturezaId.trim();
          if (trimmed === '') {
            naturezaId = null;
          } else {
            naturezaId = trimmed;
          }
        } else {
          naturezaId = String(values.naturezaId);
        }
      }

      const accountData = {
        ...values,
        id: account?.id,
        naturezaId: naturezaId,
      };

      const handleSuccess = () => {
        history.push('/financeiro/contas-pagar');
      };

      if (isEdit) {
        dispatch(
          AccountsPayableActions.updateAccountPayableRequest(accountData, handleSuccess)
        );
      } else {
        dispatch(
          AccountsPayableActions.addAccountPayableRequest(accountData, handleSuccess)
        );
      }
    },
  });

  useEffect(() => {
    if (account) {
      const naturezaId = account.naturezaId || '';
      
      formik.setValues({
        description: account.description || '',
        supplier: account.supplier || '',
        supplierId: account.supplierId || '',
        category: account.category || '',
        value: account.value || '',
        dueDate: account.dueDate || '',
        paymentDate: account.paymentDate || '',
        status: account.status || 'pending',
        notes: account.notes || '',
        naturezaId: naturezaId,
        formaPagamento: account.formaPagamento || '',
      });

      // Sincronizar input de fornecedor
      if (account.supplier) {
        setFornecedorInput(account.supplier);
        // Tentar encontrar o fornecedor na lista
        if (fornecedores.length > 0) {
          const fornecedorEncontrado = fornecedores.find(f => f.nome === account.supplier || f.id === account.supplierId);
          if (fornecedorEncontrado) {
            setFornecedorSelecionado(fornecedorEncontrado);
          }
        }
      }
      
      if (naturezaId && naturezas.length > 0) {
        const naturezaSelecionada = naturezas.find(n => String(n.id) === String(naturezaId));
        if (naturezaSelecionada) {
          setNaturezaInput(`${naturezaSelecionada.codigo || ''} - ${naturezaSelecionada.descricao || ''}`);
        }
      }
    }
  }, [account, naturezas, fornecedores]);

  // Filtrar naturezas baseado no input
  const naturezasFiltradas = useMemo(() => {
    if (!naturezaInput || naturezaInput.trim() === '') {
      return naturezas;
    }
    const searchTerm = naturezaInput.toLowerCase();
    return naturezas.filter((n) => {
      const codigo = String(n.codigo || '').toLowerCase();
      const descricao = String(n.descricao || '').toLowerCase();
      return codigo.includes(searchTerm) || descricao.includes(searchTerm);
    });
  }, [naturezaInput, naturezas]);

  // Selecionar natureza
  const handleSelecionarNatureza = (natureza) => {
    formik.setFieldValue('naturezaId', natureza.id);
    setNaturezaInput(`${natureza.codigo || ''} - ${natureza.descricao || ''}`);
    setShowDropdownNatureza(false);
  };

  // Filtrar fornecedores baseado no input
  const fornecedoresFiltrados = useMemo(() => {
    if (!fornecedorInput || fornecedorInput.trim() === '') {
      return fornecedores;
    }
    const searchTerm = fornecedorInput.toLowerCase();
    return fornecedores.filter((f) => {
      const nome = String(f.nome || '').toLowerCase();
      const nomeFantasia = String(f.nomeFantasia || '').toLowerCase();
      const cpfCnpj = String(f.cpfCnpj || '').toLowerCase();
      return nome.includes(searchTerm) || nomeFantasia.includes(searchTerm) || cpfCnpj.includes(searchTerm);
    });
  }, [fornecedorInput, fornecedores]);

  // Selecionar fornecedor
  const handleSelecionarFornecedor = (fornecedor) => {
    formik.setFieldValue('supplier', fornecedor.nome);
    formik.setFieldValue('supplierId', fornecedor.id);
    setFornecedorInput(fornecedor.nome);
    setFornecedorSelecionado(fornecedor);
    setShowDropdownFornecedor(false);
  };

  // Fechar dropdowns ao clicar fora
  useEffect(() => {
    const handleClickOutside = (event) => {
      if (dropdownNaturezaRef.current && !dropdownNaturezaRef.current.contains(event.target)) {
        setShowDropdownNatureza(false);
      }
      if (dropdownFornecedorRef.current && !dropdownFornecedorRef.current.contains(event.target)) {
        setShowDropdownFornecedor(false);
      }
    };

    document.addEventListener('mousedown', handleClickOutside);
    return () => {
      document.removeEventListener('mousedown', handleClickOutside);
    };
  }, []);

  return (
    <Container>
      <Paper
        title={isEdit ? 'EDITAR CONTA A PAGAR' : 'NOVA CONTA A PAGAR'}
        icon={<FaFileInvoiceDollar size={24} style={{ color: '#122d58', marginRight: '25px' }} />}
        rightContent={
          <div style={{ display: 'flex', alignItems: 'center', gap: '10px' }}>
            <Button
              label="Cancelar"
              onClick={() => history.push('/financeiro/contas-pagar')}
              type="button"
              width="120px"
            />
            <Button
              label={isEdit ? 'Atualizar' : 'Salvar'}
              type="button"
              width="120px"
              onClick={() => {
                formik.handleSubmit();
              }}
            />
          </div>
        }
      >
        <form onSubmit={formik.handleSubmit}>
          <WrapperBody>
            <WrapperContent>
              <InputRow>
                <WrapperInput>
                  <TextField
                    label="Descrição *"
                    name="description"
                    value={formik.values.description}
                    onChange={formik.handleChange}
                    onBlur={formik.handleBlur}
                    error={
                      formik.touched.description &&
                      Boolean(formik.errors.description)
                    }
                    helperText={
                      formik.touched.description && formik.errors.description
                    }
                    fullWidth
                    color="secondary"
                  />
                </WrapperInput>
                <WrapperInput>
                  <AutocompleteWrapper ref={dropdownFornecedorRef}>
                    <TextField
                      label="Fornecedor *"
                      name="supplier"
                      value={fornecedorInput}
                      onChange={(e) => {
                        setFornecedorInput(e.target.value);
                        setShowDropdownFornecedor(true);
                      }}
                      onFocus={() => setShowDropdownFornecedor(true)}
                      onBlur={(e) => {
                        formik.handleBlur(e);
                        // Se o valor não corresponder a nenhum fornecedor, manter o valor digitado
                        if (!fornecedorSelecionado || fornecedorSelecionado.nome !== fornecedorInput) {
                          formik.setFieldValue('supplier', fornecedorInput);
                        }
                      }}
                      error={
                        formik.touched.supplier && Boolean(formik.errors.supplier)
                      }
                      helperText={
                        formik.touched.supplier && formik.errors.supplier
                      }
                      placeholder="Digite para buscar fornecedor..."
                      fullWidth
                      color="secondary"
                    />
                    {showDropdownFornecedor && fornecedoresFiltrados.length > 0 && (
                      <AutocompleteDropdown>
                        {fornecedoresFiltrados.map((fornecedor) => (
                          <AutocompleteItem
                            key={fornecedor.id}
                            onClick={() => handleSelecionarFornecedor(fornecedor)}
                          >
                            {fornecedor.nome} {fornecedor.nomeFantasia ? `(${fornecedor.nomeFantasia})` : ''} - {fornecedor.cpfCnpj}
                          </AutocompleteItem>
                        ))}
                      </AutocompleteDropdown>
                    )}
                  </AutocompleteWrapper>
                </WrapperInput>
              </InputRow>

              <InputRow>
                <WrapperInput>
                  <TextField
                    label="Categoria"
                    name="category"
                    value={formik.values.category}
                    onChange={formik.handleChange}
                    onBlur={formik.handleBlur}
                    fullWidth
                    color="secondary"
                  />
                </WrapperInput>
                <WrapperInput>
                  <TextField
                    label="Valor *"
                    name="value"
                    type="number"
                    inputProps={{ step: '0.01', min: '0' }}
                    value={formik.values.value}
                    onChange={formik.handleChange}
                    onBlur={formik.handleBlur}
                    error={
                      formik.touched.value && Boolean(formik.errors.value)
                    }
                    helperText={formik.touched.value && formik.errors.value}
                    fullWidth
                    color="secondary"
                  />
                </WrapperInput>
              </InputRow>

              <InputRow>
                <WrapperInput>
                  <TextField
                    label="Data de Vencimento *"
                    name="dueDate"
                    type="date"
                    value={formik.values.dueDate}
                    onChange={formik.handleChange}
                    onBlur={formik.handleBlur}
                    error={
                      formik.touched.dueDate && Boolean(formik.errors.dueDate)
                    }
                    helperText={
                      formik.touched.dueDate && formik.errors.dueDate
                    }
                    fullWidth
                    color="secondary"
                    InputLabelProps={{
                      shrink: true,
                    }}
                  />
                </WrapperInput>
                <WrapperInput>
                  <TextField
                    label="Data de Pagamento"
                    name="paymentDate"
                    type="date"
                    value={formik.values.paymentDate}
                    onChange={formik.handleChange}
                    onBlur={formik.handleBlur}
                    fullWidth
                    color="secondary"
                    InputLabelProps={{
                      shrink: true,
                    }}
                  />
                </WrapperInput>
              </InputRow>

              <InputRow>
                <WrapperInput>
                  <TextField
                    label="Status *"
                    name="status"
                    select
                    value={formik.values.status}
                    onChange={formik.handleChange}
                    onBlur={formik.handleBlur}
                    error={
                      formik.touched.status && Boolean(formik.errors.status)
                    }
                    helperText={
                      formik.touched.status && formik.errors.status
                    }
                    fullWidth
                    color="secondary"
                  >
                    <MenuItem value="pending">Pendente</MenuItem>
                    <MenuItem value="paid">Paga</MenuItem>
                    <MenuItem value="overdue">Vencida</MenuItem>
                  </TextField>
                </WrapperInput>
                <WrapperInput>
                  <AutocompleteWrapper ref={dropdownNaturezaRef}>
                    <TextField
                      label="Natureza"
                      name="naturezaId"
                      value={naturezaInput}
                      onChange={(e) => {
                        setNaturezaInput(e.target.value);
                        setShowDropdownNatureza(true);
                      }}
                      onFocus={() => setShowDropdownNatureza(true)}
                      placeholder="Digite para buscar..."
                      fullWidth
                      color="secondary"
                    />
                    {showDropdownNatureza && naturezasFiltradas.length > 0 && (
                      <AutocompleteDropdown>
                        {naturezasFiltradas.map((natureza) => (
                          <AutocompleteItem
                            key={natureza.id}
                            onClick={() => handleSelecionarNatureza(natureza)}
                          >
                            {natureza.codigo || ''} - {natureza.descricao || ''}
                          </AutocompleteItem>
                        ))}
                      </AutocompleteDropdown>
                    )}
                  </AutocompleteWrapper>
                </WrapperInput>
              </InputRow>

              <InputRow>
                <WrapperInput>
                  <TextField
                    label="Forma de Pagamento"
                    name="formaPagamento"
                    select
                    value={formik.values.formaPagamento || ''}
                    onChange={formik.handleChange}
                    onBlur={formik.handleBlur}
                    fullWidth
                    color="secondary"
                  >
                    <MenuItem value="">Selecione...</MenuItem>
                    <MenuItem value="Cartao de Credito">Cartão de Crédito</MenuItem>
                    <MenuItem value="Boleto">Boleto</MenuItem>
                    <MenuItem value="Pix">Pix</MenuItem>
                  </TextField>
                </WrapperInput>
              </InputRow>

              <InputRow>
                <WrapperInput>
                  <TextField
                    label="Observações"
                    name="notes"
                    multiline
                    rows={3}
                    value={formik.values.notes}
                    onChange={formik.handleChange}
                    onBlur={formik.handleBlur}
                    fullWidth
                    color="secondary"
                  />
                </WrapperInput>
              </InputRow>
            </WrapperContent>
          </WrapperBody>
        </form>
      </Paper>
    </Container>
  );
}

export default ContaPagarForm;

